// src/components/promos/forms/PromoForm.tsx
import { useMemo, useState } from "react";
import Input from "@/components/shared/ui/Input";
import { usePublicCurrenciesList } from "@/api/features/currencies";
import CustomerAllowlistPicker from "@/components/promos/CustomerAllowlistPicker";
import PillToggleGroup from "@/components/promos/PillToggleGroup";
import type {
  PromoWithConfig,
  PromoCustomerLite,
  CreatePromoPayload,
  UpdatePromoPayload,
  PromoStatus,
  PromoDiscountType,
  PromoDiscountBasis,
  PromoCodeMode,
  PromoAudience,
} from "@/api/features/promos";

type Props = {
  mode: "create" | "edit";
  initial?: PromoWithConfig | null;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (payload: CreatePromoPayload | UpdatePromoPayload) => void;
};

const num = (v: string): number | undefined => {
  const n = Number(v);
  return v.trim() === "" || !Number.isFinite(n) ? undefined : n;
};
const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
// ISO (UTC) -> value for <input type="datetime-local"> in the viewer's locale.
const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (v: string): string | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-gray-700">{label}</span>
    {children}
    {hint ? <span className="mt-1 block text-[11px] text-gray-400">{hint}</span> : null}
  </label>
);

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select
    {...props}
    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
  />
);

export default function PromoForm({ mode, initial, submitting, error, onCancel, onSubmit }: Props) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [internalNotes, setInternalNotes] = useState(initial?.internalNotes ?? "");
  const [status, setStatus] = useState<PromoStatus>(initial?.status ?? "draft");

  const [discountType, setDiscountType] = useState<PromoDiscountType>(initial?.discountType ?? "percentage");
  const [discountValue, setDiscountValue] = useState(String(initial?.discountValue ?? ""));
  const [maxDiscountAmount, setMaxDiscountAmount] = useState(
    initial?.maxDiscountAmount != null ? String(initial.maxDiscountAmount) : ""
  );
  const [minOrderAmount, setMinOrderAmount] = useState(String(initial?.minOrderAmount ?? "0"));
  const [discountBasis, setDiscountBasis] = useState<PromoDiscountBasis>(initial?.discountBasis ?? "grand_total");
  const [currencyCode, setCurrencyCode] = useState(initial?.currencyCode ?? "");

  const [codeMode, setCodeMode] = useState<PromoCodeMode>(initial?.codeMode ?? "shared");
  const [code, setCode] = useState("");

  const [audience, setAudience] = useState<PromoAudience>(initial?.audience ?? "public");
  const [allowedCustomers, setAllowedCustomers] = useState<PromoCustomerLite[]>(
    initial?.allowedCustomers ?? []
  );
  const [firstOrderOnly, setFirstOrderOnly] = useState(Boolean(initial?.firstOrderOnly));
  const [types, setTypes] = useState<Set<"flight" | "tour" | "hotel">>(
    new Set(initial?.productScope?.types ?? ["flight", "tour", "hotel"])
  );
  const [international, setInternational] = useState<"any" | "domestic" | "international">(
    initial?.productScope?.international ?? "any"
  );
  const [channels, setChannels] = useState<Set<"web" | "mobile" | "admin">>(
    new Set(initial?.channelScope?.channels ?? ["web", "mobile", "admin"])
  );

  const [startsAt, setStartsAt] = useState(isoToLocalInput(initial?.startsAt));
  const [endsAt, setEndsAt] = useState(isoToLocalInput(initial?.endsAt));

  const [maxRedemptions, setMaxRedemptions] = useState(
    initial?.maxRedemptions != null ? String(initial.maxRedemptions) : ""
  );
  const [perUserLimit, setPerUserLimit] = useState(String(initial?.perUserLimit ?? 1));
  const [maxDiscountBudget, setMaxDiscountBudget] = useState(
    initial?.maxDiscountBudget != null ? String(initial.maxDiscountBudget) : ""
  );
  const [stackable, setStackable] = useState(Boolean(initial?.stackable));
  const [attempted, setAttempted] = useState(false);

  const { data: currencies } = usePublicCurrenciesList({ limit: 300 });

  const localError = useMemo(() => {
    if (!name.trim()) return "Name is required.";
    const dv = num(discountValue);
    if (dv == null || dv <= 0) return "Discount value must be greater than 0.";
    if (discountType === "percentage" && dv > 100) return "Percentage cannot exceed 100.";
    if (mode === "create" && codeMode === "shared" && code.trim().length < 3)
      return "A shared-code promo needs a code (min 3 chars).";
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt))
      return "End must be after start.";
    if (audience === "restricted" && allowedCustomers.length === 0)
      return "Add at least one customer for a restricted promo.";
    return null;
  }, [name, discountValue, discountType, mode, codeMode, code, startsAt, endsAt, audience, allowedCustomers]);

  const submit = () => {
    setAttempted(true);
    if (localError) return;
    // "All selected" (every option chosen) means no restriction — send an empty
    // list so the backend applies the promo everywhere.
    const typesAll = (["flight", "tour", "hotel"] as const).every((t) => types.has(t));
    const channelsAll = (["web", "mobile", "admin"] as const).every((c) => channels.has(c));
    const typeArr = typesAll ? [] : Array.from(types);
    const chanArr = channelsAll ? [] : Array.from(channels);
    const productScope =
      typeArr.length || international !== "any" ? { types: typeArr, international } : undefined;
    const channelScope = chanArr.length ? { channels: chanArr } : undefined;

    const base: CreatePromoPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      internalNotes: internalNotes.trim() || undefined,
      status,
      discountType,
      discountValue: num(discountValue)!,
      maxDiscountAmount: discountType === "percentage" ? num(maxDiscountAmount) : undefined,
      minOrderAmount: num(minOrderAmount) ?? 0,
      discountBasis,
      currencyCode: currencyCode.trim().toUpperCase() || undefined,
      audience,
      firstOrderOnly,
      allowedUserIds: audience === "restricted" ? allowedCustomers.map((c) => c.userId) : [],
      productScope,
      channelScope,
      startsAt: localInputToIso(startsAt),
      endsAt: localInputToIso(endsAt),
      maxRedemptions: num(maxRedemptions),
      perUserLimit: num(perUserLimit) ?? 1,
      maxDiscountBudget: num(maxDiscountBudget),
      stackable,
    };

    if (mode === "create") {
      onSubmit({ ...base, codeMode, code: codeMode === "shared" ? code.trim().toUpperCase() : undefined });
    } else {
      // codeMode + code are immutable after creation.
      const { ...rest } = base;
      onSubmit(rest as UpdatePromoPayload);
    }
  };

  // Only surface local validation AFTER a submit attempt (a fresh form shouldn't
  // scream "Name is required" before the user types). Server errors always show.
  const shownError = error ?? (attempted ? localError : null);

  return (
    <div className="space-y-6">
      {shownError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {shownError}
        </div>
      ) : null}

      {/* Basics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Name *">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Summer Sale 10%" />
        </Field>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown to customers" />
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as PromoStatus)}>
            {["draft", "active", "paused", "expired", "archived"].map((s) => (
              <option key={s} value={s}>{titleCase(s)}</option>
            ))}
          </Select>
        </Field>
      </div>

      {/* Discount */}
      <div className="border-t border-gray-200 pt-4">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Discount</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Field label="Type">
            <Select value={discountType} onChange={(e) => setDiscountType(e.target.value as PromoDiscountType)}>
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount</option>
            </Select>
          </Field>
          <Field label={discountType === "percentage" ? "Value (%)" : "Value (amount)"}>
            <Input type="number" min="0" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} />
          </Field>
          <Field label="Max cap" hint="% only">
            <Input
              type="number" min="0" step="0.01"
              value={maxDiscountAmount}
              disabled={discountType !== "percentage"}
              onChange={(e) => setMaxDiscountAmount(e.target.value)}
            />
          </Field>
          <Field label="Basis">
            <Select value={discountBasis} onChange={(e) => setDiscountBasis(e.target.value as PromoDiscountBasis)}>
              <option value="grand_total">Grand total</option>
              <option value="pre_tax">Pre-tax</option>
              <option value="subtotal">Subtotal</option>
              <option value="base_fare">Base fare</option>
            </Select>
          </Field>
          <Field label="Min order">
            <Input type="number" min="0" step="0.01" value={minOrderAmount} onChange={(e) => setMinOrderAmount(e.target.value)} />
          </Field>
          <Field label="Currency">
            <Select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)}>
              <option value="">Any</option>
              {(currencies ?? []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {/* Code model (create only) */}
      {mode === "create" ? (
        <div className="border-t border-gray-200 pt-4">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Code</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Code model">
              <Select value={codeMode} onChange={(e) => setCodeMode(e.target.value as PromoCodeMode)}>
                <option value="shared">Shared code (one code for all)</option>
                <option value="unique_batch">Unique batch (generate later)</option>
              </Select>
            </Field>
            {codeMode === "shared" ? (
              <Field label="Code *" hint="Case-insensitive, e.g. WELCOME10">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="WELCOME10" />
              </Field>
            ) : (
              <div className="flex items-end text-xs text-gray-500">
                Save the promo, then generate a batch of unique single-use codes from the list.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Edit: the code is IMMUTABLE after creation — customers already hold
           it and existing redemptions reference it — but an admin still needs
           to see which code this promo is, so it is shown read-only. */
        <div className="border-t border-gray-200 pt-4">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Code</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Code model">
              <Input
                value={
                  initial?.codeMode === "unique_batch"
                    ? "Unique batch (single-use codes)"
                    : "Shared code (one code for all)"
                }
                readOnly
                disabled
              />
            </Field>

            {initial?.codeMode === "shared" ? (
              <Field label="Code" hint="Codes cannot be changed after creation.">
                <Input value={initial?.codes?.[0]?.code ?? "—"} readOnly disabled />
              </Field>
            ) : (
              <Field label="Codes generated">
                <Input
                  value={`${initial?.codes?.length ?? 0} code(s)`}
                  readOnly
                  disabled
                />
              </Field>
            )}
          </div>

          {initial?.codeMode === "unique_batch" && (initial?.codes?.length ?? 0) > 0 ? (
            <div className="mt-3 max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex flex-wrap gap-1.5">
                {initial!.codes!.slice(0, 100).map((row) => (
                  <span
                    key={row.id}
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      row.redeemedCount > 0
                        ? "bg-gray-200 text-gray-500 line-through"
                        : "bg-white text-gray-800 ring-1 ring-gray-200"
                    }`}
                    title={row.redeemedCount > 0 ? "Redeemed" : "Available"}
                  >
                    {row.code}
                  </span>
                ))}
              </div>
              {(initial?.codes?.length ?? 0) > 100 ? (
                <p className="mt-2 text-xs text-gray-500">
                  Showing first 100 of {initial!.codes!.length}.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {/* Targeting */}
      <div className="border-t border-gray-200 pt-4">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Targeting</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Audience">
            <Select value={audience} onChange={(e) => setAudience(e.target.value as PromoAudience)}>
              <option value="public">Public (anyone)</option>
              <option value="restricted">Restricted (selected customers)</option>
            </Select>
          </Field>
          {audience === "restricted" ? (
            <div className="sm:col-span-2">
              <Field label="Allowed customers *">
                <CustomerAllowlistPicker value={allowedCustomers} onChange={setAllowedCustomers} />
              </Field>
            </div>
          ) : null}
          <Field label="Applies to products">
            <PillToggleGroup
              options={[
                { value: "flight", label: "Flight" },
                { value: "tour", label: "Tour" },
                { value: "hotel", label: "Hotel" },
              ]}
              value={types}
              onChange={setTypes}
              allLabel="All products"
            />
          </Field>
          <Field label="Travel scope">
            <Select value={international} onChange={(e) => setInternational(e.target.value as any)}>
              <option value="any">Any</option>
              <option value="domestic">Domestic only</option>
              <option value="international">International only</option>
            </Select>
          </Field>
          <Field label="Channels">
            <PillToggleGroup
              options={[
                { value: "web", label: "Web" },
                { value: "mobile", label: "Mobile" },
                { value: "admin", label: "Admin" },
              ]}
              value={channels}
              onChange={setChannels}
              allLabel="All channels"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={firstOrderOnly} onChange={(e) => setFirstOrderOnly(e.target.checked)} />
            First booking only
          </label>
        </div>
      </div>

      {/* Validity + limits */}
      <div className="border-t border-gray-200 pt-4">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Validity & limits</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Starts at">
            <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="Ends at">
            <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
          <Field label="Total redemptions cap" hint="Blank = unlimited">
            <Input type="number" min="1" step="1" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} />
          </Field>
          <Field label="Per-user limit" hint="0 = unlimited">
            <Input type="number" min="0" step="1" value={perUserLimit} onChange={(e) => setPerUserLimit(e.target.value)} />
          </Field>
          <Field label="Total discount budget" hint="Blank = unlimited">
            <Input type="number" min="0" step="0.01" value={maxDiscountBudget} onChange={(e) => setMaxDiscountBudget(e.target.value)} />
          </Field>
          <label className="flex items-center gap-2 pt-6 text-sm">
            <input type="checkbox" checked={stackable} onChange={(e) => setStackable(e.target.checked)} />
            Stackable with other promos
          </label>
        </div>
      </div>

      <Field label="Internal notes" hint="Not shown to customers">
        <Input value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
      </Field>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {submitting ? "Saving…" : mode === "create" ? "Create promo" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
